import { logger } from '@utils';
import { SagaIterator, Channel } from 'redux-saga';
import { call, put, select, take } from 'redux-saga/effects';
import * as FileWebsocket from './file.websocket';
import * as FileActions from '@services/file/file.actions';
import * as fileHttp from './file.http';
import * as UserActions from '@services/user/user.actions';
import { IFileDesc, IUploadFile, IStuffToUpload, IStuffBeingUploaded } from '@app-types/websocket/file.types';
import { cacheDelete, cachePruneOlderThan } from './upload.cache';
import { commonWebSocket } from '@services/common/common.constants';
import { UPLOAD_TOO_LARGE_WARNING, isUploadTooLargeError, uploadFailureMessage } from './upload.errors';

const UPLOAD_SLOT_TIMEOUT_MS = 30000;
const UPLOAD_SLOT_RETRY_MS = 3000;
const MAX_UPLOAD_SLOT_RETRY_AGE_MS = 10000;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface IUploadSlotState {
  dataToUpload: IStuffToUpload[];
  uploadSlots: IStuffBeingUploaded[];
}

const selectUploadSlotState = (state: any): IUploadSlotState => ({
  dataToUpload: state.file && Array.isArray(state.file.dataToUpload) ? state.file.dataToUpload : [],
  uploadSlots: state.file && Array.isArray(state.file.uploadSlots) ? state.file.uploadSlots : [],
});

const selectUploadContext = (state: any): { authorization?: string; familyId?: number } => {
  const session = state?.user?.session;
  const user = session?.data;
  const resources = Array.isArray(user?.resources) ? user.resources : [];
  const selectedFamilyIndex = Number.isInteger(session?.selectedFamily) ? session.selectedFamily : 0;
  const selectedResource = resources[selectedFamilyIndex] || resources[0];

  return {
    authorization: user?.authorization,
    familyId: selectedResource?.id,
  };
};

function isWaitingForUploadSlot(upload: IUploadFile, state: IUploadSlotState): boolean {
  if (!upload || upload.internalId === undefined) {
    return false;
  }

  const stillWaiting = state.dataToUpload.some((file) => file.internalId === upload.internalId);
  const hasSlot = state.uploadSlots.some((file) => file.internalId === upload.internalId);
  return stillWaiting && !hasSlot;
}

export function* requestChatFileUploadSlot(action: { type: string; payload: IUploadFile }): SagaIterator {
  try {
    if (!action.payload || action.payload.internalId === undefined
      || action.payload.name === undefined || action.payload.size === undefined) {
      if (action.payload && action.payload.internalId !== undefined) {
        cacheDelete(action.payload.internalId);
      }
      return;
    }

    cachePruneOlderThan(UPLOAD_SLOT_TIMEOUT_MS);
    const upload:IUploadFile = {
      ...action.payload,
      requestedAt: Date.now(),
    };
    const uploadContext = (yield select(selectUploadContext)) as unknown as { authorization?: string; familyId?: number };

    if (!uploadContext.authorization || !uploadContext.familyId) {
      cacheDelete(upload.internalId);
      yield put(UserActions.displayErrorMessage("Your upload was unsuccessful, please report to support."));
      yield put(FileActions.uploadFailed({ ...upload, loaded: 0 } as any));
      return;
    }

    yield put(FileActions.storeFileWhileAwaitingUploadslot(upload));

    const slot = yield call(fileHttp.requestChatUploadSlot, upload, uploadContext.familyId, uploadContext.authorization);
    yield put({ type: commonWebSocket.special.FILE_UPLOAD, payload: slot });

    yield call(sleep, UPLOAD_SLOT_TIMEOUT_MS);
    const timeoutState = (yield select(selectUploadSlotState)) as unknown as IUploadSlotState;
    if (isWaitingForUploadSlot(upload, timeoutState)) {
      cacheDelete(upload.internalId);
      yield put(UserActions.displayErrorMessage("Your upload was unsuccessful, please report to support."));
      yield put(FileActions.uploadFailed({ ...upload, loaded: 0 } as any));
    }
  } catch (error) {
    logger.error(error);
    if (action.payload && action.payload.internalId !== undefined) {
      cacheDelete(action.payload.internalId);
    }
    yield put(FileActions.uploadFailed({ ...action.payload, loaded: 0 } as any));
    yield put(UserActions.displayErrorMessage(uploadFailureMessage(
      error,
      "Your upload was unsuccessful, please report to support."
    )));
  }
}

export function* uploadSlotRejected(action: {
  type: string;
  payload: {internalId:number; errorMessage:string};
}): SagaIterator {
  if (!action.payload) return;

  cacheDelete(action.payload.internalId);
  const message = isUploadTooLargeError(action.payload.errorMessage)
    ? UPLOAD_TOO_LARGE_WARNING
    : action.payload.errorMessage;

  if (message) {
    yield put(UserActions.displayErrorMessage(message));
  }
}

export function* requestFileUpload(action: { type: string; payload: IUploadFile }): SagaIterator {
  try {
    if (!action.payload || action.payload.internalId === undefined
      || action.payload.name === undefined || action.payload.size === undefined) {
      if (action.payload && action.payload.internalId !== undefined) {
        cacheDelete(action.payload.internalId);
      }
      return;
    }

    cachePruneOlderThan(UPLOAD_SLOT_TIMEOUT_MS);
    const uploadRequestedAt = Date.now();
    const upload:IUploadFile = {
      ...action.payload,
      requestedAt: uploadRequestedAt,
    };
    const desc: IFileDesc = {
      fileName: upload.name,
      fileSize: upload.size,
      internalId: "" + upload.internalId,
    };
    yield put(FileActions.storeFileWhileAwaitingUploadslot(upload));
    yield call(FileWebsocket.requestFileUpload, desc);
    yield call(sleep, UPLOAD_SLOT_RETRY_MS);

    const retryState = (yield select(selectUploadSlotState)) as unknown as IUploadSlotState;
    if (isWaitingForUploadSlot(upload, retryState)
      && Date.now() - uploadRequestedAt <= MAX_UPLOAD_SLOT_RETRY_AGE_MS) {
      yield call(FileWebsocket.requestFileUpload, desc);
    }

    yield call(sleep, UPLOAD_SLOT_TIMEOUT_MS - UPLOAD_SLOT_RETRY_MS);

    const timeoutState = (yield select(selectUploadSlotState)) as unknown as IUploadSlotState;
    if (isWaitingForUploadSlot(upload, timeoutState)) {
      cacheDelete(upload.internalId);
      yield put(UserActions.displayErrorMessage("Your upload was unsuccessful, please report to support."));
      yield put(FileActions.uploadFailed({ ...upload, loaded: 0 } as any));
    }
  } catch (error) {
    logger.error(error);
    if (action.payload && action.payload.internalId !== undefined) {
      cacheDelete(action.payload.internalId);
    }
    yield put(FileActions.uploadFailed({ ...action.payload, loaded: 0 } as any));
  }
}

export function* internalUploadFileAfterGivenSlot(action:{type: string;payload:IStuffBeingUploaded}): SagaIterator {
  try {
    const upload: IStuffBeingUploaded = action.payload;
    if (upload && upload.url) {
      const channel = yield call(fileHttp.uploadFile, upload);
      try {
        while (true) {
          const result: any = yield take(channel);
          if (result.progress) {
            yield put(FileActions.uploadProgress(result.progress));
          }
          if (result.result) {
            yield put(FileActions.uploadSuccess({ ...action.payload, loaded: 100 }));
          }
          if (result.error) {
            yield put(FileActions.uploadFailed({ ...action.payload, loaded: 0 }));
            if (isUploadTooLargeError(result.error)) {
              yield put(UserActions.displayErrorMessage(UPLOAD_TOO_LARGE_WARNING));
            }
          }
        }
      } finally {
        // channel.close();
      }
    } else {
      if (action.payload && action.payload.internalId !== undefined) {
        cacheDelete(action.payload.internalId);
        console.log("internalUploadFileAfterGivenSlot: no upload url for internalId "+action.payload.internalId);
      } else {
        console.log("internalUploadFileAfterGivenSlot: no upload url");
      }
    }
  } catch (error) {
    logger.error(error);
    if (action.payload && action.payload.internalId !== undefined) {
      cacheDelete(action.payload.internalId);
    }
    yield put(FileActions.uploadFailed({ ...action.payload, loaded: 0 }));
  }
}



// WEBPACK FOOTER //
// ./src/services/file/file.saga.ts